import { FC, forwardRef, useImperativeHandle, useRef } from 'react';


import env from '@eneco/common/env';
import logger from '@eneco/common/log';
import { I18nProvider, useTranslation } from '@eneco/i18n';
import { Grid, InputError } from '@eneco/sparky';
import { useValidationErrorEvent } from '@eneco/sparky/hooks';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import RichText from '../RichText/RichText';

const RECAPTCHA_SITE_KEY = env('RECAPTCHA_SITE_KEY');

export type RecaptchaRef = {
  verify: () => Promise<string>;
} | null;

type RecaptchaProps = {
  hasError?: boolean;
  onVerifyChange?: (token: string) => void;
};

const RecaptchaDisclaimer: FC = () => {
  const { t } = useTranslation();
  return (
    <RichText
      html={t('disclaimer.text', {
        privacyPolicyText: t('disclaimer.privacyPolicy.text'),
        privacyPolicyUrl: t('disclaimer.privacyPolicy.url'),
        termsOfServiceText: t('disclaimer.termsOfService.text'),
        termsOfServiceUrl: t('disclaimer.termsOfService.url'),
      })}></RichText>
  );
};

const RecaptchaError: FC<{ hasError?: boolean }> = ({ hasError }) => {
  const { t } = useTranslation();
  const errorRef = useRef<HTMLDivElement>(null);

  const error = hasError ? t('tokenError') : undefined;

  useValidationErrorEvent({ error, name: 'recaptchaToken' }, errorRef);

  if (!error) return null;

  logger.error('B47m4n', 'Unexpected undefined recaptcha token while validating form');

  return (
    <Grid.Item ref={errorRef}>
      <InputError hasArrow={false}>{error}</InputError>
    </Grid.Item>
  );
};

const RecaptchaInner = forwardRef<RecaptchaRef, RecaptchaProps>(({ hasError, onVerifyChange }: RecaptchaProps, ref) => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  useImperativeHandle(ref, () => ({
    verify: async () => {
      if (!executeRecaptcha) {
        logger.error('B47m4n', 'executeRecaptcha not available');
        return '';
      }

      const token = await executeRecaptcha('verify');
      onVerifyChange?.(token);
      return token;
    },
  }));

  return (
    <I18nProvider dictionary={locale => import(`./content/${locale}.json`)}>
      <Grid gap="4">
        <Grid.Item>
          <RecaptchaDisclaimer />
        </Grid.Item>
        <RecaptchaError hasError={hasError} />
      </Grid>
    </I18nProvider>
  );
});

export const Recaptcha = forwardRef<RecaptchaRef, RecaptchaProps>((props, ref) => {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY} useEnterprise={true}>
      <RecaptchaInner {...props} ref={ref} />
    </GoogleReCaptchaProvider>
  );
});
